import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  useCreateManifestation,
  useDeleteManifestation,
  useManifestations,
} from '../../api/hooks';
import type { Manifestation } from '../../api/types';
import { Button, EmptyState, Field, Input, ROW, Section, Spinner, TABLE, TD, TH, THEAD_ROW } from '../ui';
import { useToast } from '../toast';

const EMPTY: Omit<Manifestation, 'idManif'> = {
  libelle: '',
  genre: 'SPECTACLE ET COMEDIE MUSICALE',
  sousGenre: 'CABARET',
  urlVisuel: 'https://www.ticketmaster.fr/statics/genre/spectacle_SC.jpg',
  infosProducteur: null,
  numeroLicence: null,
  infoSpec: null,
};

export default function ManifestationsTab() {
  const toast = useToast();
  const { data, isPending } = useManifestations();
  const create = useCreateManifestation();
  const remove = useDeleteManifestation();
  const [form, setForm] = useState(EMPTY);

  return (
    <div className="space-y-4">
      <Section title="Nouvelle manifestation">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Libellé">
            <Input
              aria-label="Libellé de la manifestation"
              value={form.libelle}
              onChange={(e) => setForm({ ...form, libelle: e.target.value })}
            />
          </Field>
          <Field label="Genre">
            <Input aria-label="Genre" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} />
          </Field>
          <Field label="Sous-genre">
            <Input
              aria-label="Sous-genre"
              value={form.sousGenre}
              onChange={(e) => setForm({ ...form, sousGenre: e.target.value })}
            />
          </Field>
          <Field label="URL du visuel">
            <Input
              aria-label="URL du visuel"
              value={form.urlVisuel}
              onChange={(e) => setForm({ ...form, urlVisuel: e.target.value })}
              className="w-96"
            />
          </Field>
          <Button
            variant="primary"
            size="sm"
            className="mb-1"
            disabled={create.isPending || !form.libelle}
            onClick={() =>
              create.mutate(form, {
                onSuccess: () => {
                  toast.success('Manifestation créée');
                  setForm(EMPTY);
                },
                onError: (err) => toast.error(err),
              })
            }
          >
            Créer
          </Button>
        </div>
      </Section>

      <Section title="Manifestations">
        {isPending ? <Spinner /> : null}
        {!isPending && !data?.length ? <EmptyState>Aucune manifestation.</EmptyState> : null}
        {data?.length ? (
          <table className={TABLE}>
            <thead>
              <tr className={THEAD_ROW}>
                <th className={TH}>idManif</th>
                <th className={TH}>Libellé</th>
                <th className={TH}>Genre</th>
                <th className={TH}>Sous-genre</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {data.map((manif) => (
                <tr key={manif.idManif} className={ROW}>
                  <td className={`${TD} font-mono text-xs text-outline`}>{manif.idManif}</td>
                  <td className={TD}>{manif.libelle}</td>
                  <td className={TD}>{manif.genre}</td>
                  <td className={TD}>{manif.sousGenre}</td>
                  <td className={TD}>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Supprimer ${manif.libelle}`}
                      onClick={() =>
                        remove.mutate(manif.idManif, {
                          onSuccess: () => toast.success('Manifestation supprimée'),
                          onError: (err) => toast.error(err),
                        })
                      }
                    >
                      <Trash2 size={15} aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Section>
    </div>
  );
}
